import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

const PAGE_SIZE = 24;

export const useAdminPosts = (fetchAdmin, toast, paused, profileId = 'all') => {
  const [data, setData] = useState({ posts: [], total: 0, page: 1, has_more: false });
  const [profiles, setProfiles] = useState([]);
  const [profileError, setProfileError] = useState('');
  const [query, setQuery] = useState({ status: 'all', platform: 'all', user_id: profileId, date_range: 'all', page: 1 });
  useEffect(() => {
    setQuery(prev => prev.user_id === profileId ? prev : { ...prev, user_id: profileId, page: 1 });
  }, [profileId]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [syncError, setSyncError] = useState('');
  const [loadedKey, setLoadedKey] = useState(null);
  const cache = useRef(new Map());
  const listRequest = useRef(null);
  const syncRequest = useRef(null);
  const profilesRequest = useRef(null);
  const sequence = useRef(0);
  const currentData = useRef(data);
  currentData.current = data;
  const queryKey = useMemo(() => {
    const params = new URLSearchParams({ ...query, limit: PAGE_SIZE, q: debouncedSearch });
    if (query.user_id === 'all') params.delete('user_id');
    return params.toString();
  }, [query, debouncedSearch]);
  const currentKey = useRef(queryKey);
  currentKey.current = queryKey;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setQuery(prev => prev.page === 1 ? prev : { ...prev, page: 1 });
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const loadProfiles = useCallback(async () => {
    profilesRequest.current?.abort();
    const controller = new AbortController();
    profilesRequest.current = controller;
    setProfileError('');
    try {
      const r = await fetchAdmin('/api/admin-panel/post-profiles', { signal: controller.signal });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not load profiles');
      if (!controller.signal.aborted) setProfiles(d.users || []);
    } catch (e) { if (e.name !== 'AbortError') setProfileError(e.message); }
  }, [fetchAdmin]);

  useEffect(() => { loadProfiles(); return () => profilesRequest.current?.abort(); }, [loadProfiles]);

  const load = useCallback(async (opts = {}) => {
    if (currentKey.current !== queryKey) return;
    listRequest.current?.abort();
    const controller = new AbortController();
    listRequest.current = controller;
    const requestNumber = ++sequence.current;
    const cached = cache.current.get(queryKey);
    if (cached) setData(cached);
    else if (!opts.silent) setData({ posts: [], total: 0, page: query.page, has_more: false });
    setLoading(!cached && !opts.silent);
    setRefreshing(!!cached || !!opts.silent);
    setError('');
    try {
      const r = await fetchAdmin(`/api/admin-panel/all-posts?${queryKey}`, { signal: controller.signal });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Could not load posts');
      if (controller.signal.aborted || currentKey.current !== queryKey || requestNumber !== sequence.current) return;
      cache.current.set(queryKey, d);
      if (cache.current.size > 20) cache.current.delete(cache.current.keys().next().value);
      setData(d);
      setLoadedKey(queryKey);
      if (d.page !== query.page) setQuery(prev => ({ ...prev, page: d.page }));
    } catch (e) {
      if (e.name !== 'AbortError' && currentKey.current === queryKey && requestNumber === sequence.current) setError(e.message);
    } finally {
      if (currentKey.current === queryKey && requestNumber === sequence.current) { setLoading(false); setRefreshing(false); }
    }
  }, [fetchAdmin, queryKey, query.page]);

  useEffect(() => {
    syncRequest.current?.abort();
    setSyncing(false);
    setSyncError('');
    load();
    return () => { listRequest.current?.abort(); syncRequest.current?.abort(); };
  }, [load]);

  const syncVisible = useCallback(async (force = false) => {
    const ids = currentData.current.posts.map(p => p.id);
    if (!ids.length || syncRequest.current || document.hidden || paused) return;
    const controller = new AbortController();
    syncRequest.current = controller;
    setSyncing(true);
    setSyncError('');
    try {
      const r = await fetchAdmin('/api/admin-panel/posts/sync-visible', {
        method: 'POST', signal: controller.signal,
        body: JSON.stringify({ post_ids: ids, user_id: query.user_id === 'all' ? null : query.user_id, force }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Status refresh unavailable');
      if (controller.signal.aborted || currentKey.current !== queryKey) return;
      setSyncError(d.partial ? 'Some provider statuses are unavailable. Saved posts are shown.' : '');
      if (force) toast(d.partial ? 'Some statuses could not be refreshed' : 'Visible posts are up to date', !d.partial);
      cache.current.clear();
      await load({ silent: true });
    } catch (e) {
      if (e.name !== 'AbortError' && currentKey.current === queryKey) setSyncError(e.message);
    } finally {
      if (syncRequest.current === controller) syncRequest.current = null;
      if (currentKey.current === queryKey) setSyncing(false);
    }
  }, [fetchAdmin, query.user_id, queryKey, load, toast, paused]);

  useEffect(() => {
    if (loadedKey !== queryKey || paused) return undefined;
    const initial = setTimeout(() => syncVisible(), 400);
    const interval = setInterval(() => {
      if (!document.hidden) { load({ silent: true }); syncVisible(); }
    }, 60000);
    return () => { clearTimeout(initial); clearInterval(interval); };
  }, [loadedKey, queryKey, paused, syncVisible, load]);

  const setFilter = (field, value) => setQuery(prev => ({ ...prev, [field]: value, page: 1 }));
  return { ...data, profiles, profileError, loadProfiles, loading, refreshing, syncing, error, syncError,
    query, queryKey, search, setSearch, setFilter, setPage: page => setQuery(prev => ({ ...prev, page })),
    load: () => { cache.current.clear(); return load(); }, syncVisible };
};